import '../error_reporter.js'
import '@hotwired/turbo-rails'
import './application.css'
import '../theme_toggle.js'

import { createApp, watch } from 'vue'
import PoolLines from '../components/PoolLines.vue'
import EmailEditor from '../components/EmailEditor.vue'
import EmailSectionsEditor from '../components/EmailSectionsEditor.vue'
import { enqueue, qsQueue } from '../quickset.js'
import { seedEmail, clearEmail, emailHtml, emailReady } from '../email_editor.js'
import { contactRowHtml } from '../contact_row.js'
import { orderInstallers, installerLabel } from '../installer_options.js'
import { depotOptionDisabled, captionUnit, contactDistanceParts } from '../depot_options.js'

const STATES = ['QLD', 'NSW', 'VIC', 'SA', 'WA', 'ACT']

// Returns the CSRF token string, or '' when forgery protection is disabled
// (e.g. test environment), so fetch calls don't throw on a null meta element.
function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content ?? ''
}

// ── Vue mount ──────────────────────────────────────────────────────────────────

let hasCalculated = false
let calcTimer     = null

// The Vue apps are tracked at module scope so they can be torn down on Turbo
// navigation. Turbo Drive swaps <body> without reloading this module, so without
// an explicit unmount the previous PoolLines instance stays alive (detached) and
// keeps watching the shared qsQueue — a leak that also breaks the next page,
// because the stale watcher consumes+clears the queue before the fresh, visible
// instance can apply a command.
let poolApp     = null
let emailApp    = null
let sectionsApp = null

function mountVue() {
  const mp = document.getElementById('pool-lines-app')
  if (mp && !mp._poolApp) {
    const pools = JSON.parse(mp.dataset.pools || '{}')
    const mfg   = mp.dataset.manufacturer || ''
    poolApp     = createApp(PoolLines, { poolsByManufacturer: pools, manufacturer: mfg })
    mp._poolApp = poolApp.mount(mp)
  }
  const ed = document.getElementById('email-editor')
  if (ed && !ed._emailApp) {
    emailApp     = createApp(EmailEditor)
    ed._emailApp = emailApp.mount(ed)
  }
  // Settings → Email Template: the main-document editor + dynamic-block field
  // editors. Mounted here (before the pricing-page guard below) because the
  // settings page has no #pool-lines-app.
  const se = document.getElementById('email-sections-app')
  if (se && !se._sectionsApp) {
    const editorState = JSON.parse(se.dataset.editorState || '{}')
    const previewUrl  = se.dataset.previewUrl || ''
    sectionsApp       = createApp(EmailSectionsEditor, { editorState, previewUrl })
    se._sectionsApp   = sectionsApp.mount(se)
  }
  if (!mp) return
  // Pool keys that can never ship via Cranbourne (ADR-0010) — server-rendered
  // beside data-pools so this module never hardcodes its own list.
  cranbourneIneligible = new Set(JSON.parse(mp.dataset.cranbourneIneligible || '[]'))
  initContactSearch()
  renderInstallerOptions()
  renderDepotCaption()
  updateDistanceUnitHint()
  syncDepotSelectDisabled()
  // A HubSpot "Activate Nimbus" deep link arrives pre-selected: focus Call Notes
  // (as a manual select does). Otherwise focus the search box, as before.
  if (!prefillSelectedContact()) {
    document.getElementById('contact-search-input')?.focus()
  }
}

// HubSpot "Activate Nimbus" deep link (GET /?hubspot_contact_id=<id>): the server
// looks the contact up and embeds it as JSON on #contact-selected. Apply it via
// the same setContact path a manual search-select uses, so the contact card,
// HubSpot back-link, state and auto-distance all populate identically.
// Returns true if a contact was applied.
function prefillSelectedContact() {
  const el  = document.getElementById('contact-selected')
  const raw = el?.dataset.prefillContact
  if (!raw) return false
  // Consume once: a later Turbo re-render must not re-apply a stale prefill.
  delete el.dataset.prefillContact
  let contact
  try { contact = JSON.parse(raw) } catch { return false }
  if (!contact || !contact.id) return false
  const st = setContact(contact)
  focusCallNotes()
  if (st) setFeedback(st.text, st.tone)
  return true
}

// Tear down the Vue apps before Turbo renders the next page, so no detached
// instance survives to watch qsQueue. Also drop any unconsumed queue commands so
// they can't leak onto the next page.
function unmountVue() {
  poolApp?.unmount()
  emailApp?.unmount()
  sectionsApp?.unmount()
  poolApp = emailApp = sectionsApp = null
  qsQueue.value = []
}

// ── Forced SA-direct origin (ADR-0010) ────────────────────────────────────────
// The browser NEVER decides pricing (the server re-resolves the forced origin
// from the posted pool keys on every calculate/draft); these surfaces only make
// the distance NUMBER and its unit label correct for staff.

let cranbourneIneligible = new Set()

// The last successful /distance response, so forced-ness changes (pool line or
// state edits) can re-apply the right value WITHOUT a new Mapbox call. Cleared
// whenever the contact changes/clears (clearContactDistance). Never set on the
// manual-distance path, so hand-typed figures are never clobbered. Declared in
// this early section (it executes before the module-evaluation mountVue() call)
// because the prefill-contact path reaches clearContactDistance synchronously.
let lastDistanceLookup = null

// Forced-ness at the last cache apply, plus whether staff have hand-edited the
// Distance field since then. Together they make re-applies conservative
// (manual-edits-win): only a genuine forced-ness FLIP — a Cranbourne-ineligible
// pool entering or leaving a VIC order — may rewrite the field; any other
// recalc trigger (marbletech toggle, notes tick, same-forcedness pool/state
// change) defers to whatever is in the field, especially a staff override.
let lastAppliedForced = null
let distanceManuallyEdited = false

// Monotonic /distance request id: a response only applies while its seq is
// current. Bumped by every lookup AND by clearContactDistance, so a late reply
// for a cleared/switched contact can never resurrect the distance surfaces.
// Declared in this early section for the same prefill-path reason as
// lastDistanceLookup above.
let distanceReqSeq = 0

// True only after a genuine staff gesture on the Depot select. While set,
// lookups pin the distance origin to that depot (depot_id is posted to
// /distance); cleared whenever the contact, manufacturer, or lookup context
// changes so a stale override is never silently re-sent (ADR-0011).
let depotManuallySelected = false

// True iff the delivery is forced SA-direct: state VIC and any pool line holds
// a Cranbourne-ineligible key (same DOM reads submitCalculation uses).
function forcedSaDirect() {
  if ((document.getElementById('state-select')?.value || '') !== 'VIC') return false
  return Array.from(
    document.querySelectorAll('[name^="pool_lines"][name$="[pool_key]"]')
  ).some(el => cranbourneIneligible.has(el.value))
}

// Duplicated-string rule: UNIT_HINT_DEFAULT must stay byte-identical to the
// ERB default on #distance-unit-hint in app/views/prices/new.html.erb.
const UNIT_HINT_DEFAULT = 'km or hrs (VIC)'
const UNIT_HINT_FORCED  = 'km — ships direct from Burton SA'
// Staff overrode a VIC order to SA-direct with NO Burton candidate in hand
// (manual path): the Distance field value is priced as km-from-Burton, so the
// hint must say so (ADR-0013).
const UNIT_HINT_SA_OVERRIDE = 'km — priced direct from Burton SA (staff choice)'
// Named feedback for a VIC lookup whose Burton route failed: the comparison is
// unavailable and the quote stands on Cranbourne hours (ADR-0013).
const BURTON_UNROUTABLE_FEEDBACK = 'No route from Burton SA — priced from Cranbourne only'

function updateDistanceUnitHint() {
  const el = document.getElementById('distance-unit-hint')
  if (!el) return
  if (forcedSaDirect()) { el.textContent = UNIT_HINT_FORCED; return }
  const override = document.getElementById('origin-override')?.value || ''
  const hasSaKm  = (document.getElementById('sa-direct-km')?.value || '') !== ''
  el.textContent = override === 'sa_direct' && !hasSaKm ? UNIT_HINT_SA_OVERRIDE : UNIT_HINT_DEFAULT
}

// ── VIC cheapest-origin comparison state (ADR-0013) ──────────────────────────
// Three hidden inputs on the form carry the comparison across POSTs: the two
// geocoded candidates (#vic-hours + #sa-direct-km, written only by a
// non-forced VIC lookup apply) and the staff flip (#origin-override, written
// only by the results-frame buttons). The browser never decides the origin —
// the server re-runs DeliveryOriginResolver.choose on every calculate/draft.

function setOriginCandidates(vicHours, saKm) {
  const vh = document.getElementById('vic-hours')
  const sk = document.getElementById('sa-direct-km')
  if (vh) vh.value = vicHours ?? ''
  if (sk) sk.value = saKm ?? ''
  updateDistanceUnitHint()
}

// The candidates clear on every path that invalidates the geocoded pair:
// trusted manual Distance edit, contact change/clear (clearContactDistance),
// state change, and Clear All — a stale Burton km must never ride into a
// later quote.
function clearOriginCandidates() {
  setOriginCandidates(null, null)
}

function setOriginOverride(value) {
  const el = document.getElementById('origin-override')
  if (el) el.value = value ?? ''
  updateDistanceUnitHint()
}

// The override sticks across recalculations (it lives in the form) and clears
// with the customer context: state change, contact change/clear, Clear All.
function clearOriginOverride() {
  setOriginOverride('')
}

// Every pricing POST (calculate + email draft) carries the comparison state,
// so the results panel and the composed email always agree on the origin.
function appendOriginParams(fd) {
  fd.append('vic_hours',       document.getElementById('vic-hours')?.value || '')
  fd.append('sa_direct_km',    document.getElementById('sa-direct-km')?.value || '')
  fd.append('origin_override', document.getElementById('origin-override')?.value || '')
}

// Pool-line or state changes can flip forced-ness: refresh the unit hint and —
// ONLY when forced-ness actually flipped since the last apply — re-apply the
// cached contact lookup (no new Mapbox call) so a forced order never sits on a
// Cranbourne hours figure. Non-flip triggers never touch the Distance field:
// staff may have manually overridden the looked-up figure (manual-edits-win).
function refreshForcedOrigin() {
  updateDistanceUnitHint()
  if (lastDistanceLookup && forcedSaDirect() !== lastAppliedForced) applyDistanceLookup()
  syncDepotSelectDisabled()
}

// ── Depot select (staff distance-origin override, ADR-0011) ───────────────────

// Programmatic write to the Depot select. Never dispatches change, so auto-sets
// from a lookup response can't re-trigger a lookup loop. An id the select
// doesn't know falls back to Auto.
function setDepotSelect(depotId) {
  const sel = document.getElementById('depot-select')
  if (!sel) return
  sel.value = depotId == null ? '' : String(depotId)
}

// Option availability, per the pricing-state rules:
//   - forced SA-direct: the whole select is locked — the origin is Burton by
//     ADR-0010 and the server re-resolves it on every calculate anyway.
//   - VIC (not forced): VIC prices by HOURS from the hours depot, so km-mode
//     depots are disabled — a km figure must never feed FLAT hours pricing.
//   - everything else: all depots selectable.
// Guard only: the server stays permissive about combinations (the browser
// never decides pricing).
function syncDepotSelectDisabled() {
  const sel = document.getElementById('depot-select')
  if (!sel) return
  const forced = forcedSaDirect()
  const state = document.getElementById('state-select')?.value || ''
  sel.disabled = forced
  Array.from(sel.options).forEach(opt => {
    opt.disabled = depotOptionDisabled(
      { value: opt.value, unitMode: opt.dataset.unitMode }, { state, forced })
  })
  // A state flip can leave a now-disabled option selected — snap back to Auto
  // and drop the override so the next lookup reverts to nearest-depot.
  if (!forced && sel.selectedOptions[0]?.disabled) {
    sel.value = ''
    depotManuallySelected = false
  }
}

// ── Derived "distance from depot" caption ─────────────────────────────────────
// Renders "{distance} {km|hrs} from {depot label}" into #depot-caption and the
// selected PRICING state into #depot-state-badge. With the Depot select on
// Auto, the origin reads as the generic "Depot, {STATE}"; with a depot chosen
// it is that depot's label, which carries the depot's OWN state — e.g.
// "Freedom Yatala (QLD)". This is a SEPARATE surface from the contact-distance
// line (#contact-distance), which uses the looked-up depot name. Read-only:
// never writes the inputs or dispatches events, so it cannot perturb the
// contact-distance lifecycle.
function renderDepotCaption() {
  const caption = document.getElementById('depot-caption')
  const badge   = document.getElementById('depot-state-badge')
  if (!caption) return

  const state = document.getElementById('state-select')?.value || ''
  const raw   = document.getElementById('distance-input')?.value || ''
  const depotOpt = document.getElementById('depot-select')?.selectedOptions?.[0]
  const chosen   = depotOpt && depotOpt.value ? depotOpt : null
  // VIC is the only FLAT/hours zone in @states; everything else is km — but a
  // forced SA-direct order (ADR-0010) is km even for VIC, and so is a VIC
  // order pinned to a km-mode depot (ADR-0011).
  const unit = captionUnit({ state, forced: forcedSaDirect(),
    chosenUnitMode: chosen ? chosen.dataset.unitMode : null })
  const num   = parseFloat(raw)
  const hasDist = raw.trim() !== '' && Number.isFinite(num) && num > 0
  const suffix  = state ? `, ${state}` : ''
  const origin  = chosen ? chosen.textContent : `Depot${suffix}`

  caption.textContent = hasDist
    ? `${num} ${unit} from ${origin}`
    : `— from ${origin}`

  if (badge) {
    if (state) {
      badge.textContent = state
      badge.classList.remove('hidden')
    } else {
      badge.textContent = ''
      badge.classList.add('hidden')
    }
  }
}

// Re-run calculation after a short debounce — only if a result is already shown.
function autoCalculate() {
  if (!hasCalculated) return
  clearTimeout(calcTimer)
  calcTimer = setTimeout(submitCalculation, 200)
}

document.addEventListener('turbo:before-render', unmountVue)
document.addEventListener('turbo:load', mountVue)
if (document.readyState !== 'loading') mountVue()

// ── Input change → auto-recalculate ───────────────────────────────────────────

document.addEventListener('change', e => {
  const t = e.target
  if (
    t.id === 'manufacturer-select' ||
    t.id === 'state-select' ||
    t.name === 'delivery_type_display' ||
    t.name?.startsWith('pool_lines')
  ) autoCalculate()
  // Record manual edits to notes-controlled fields so the next re-parse won't
  // clobber them (manual-edits-win). Only genuine user gestures count — the
  // notes-apply path uses a programmatic guard (see applyNotesResult) so its
  // own writes don't register as overrides.
  if (!notesApplying) {
    if (t.name === 'delivery_type_display') manualOverrides.add('delivery')
    if (t.name?.startsWith('pool_lines')) {
      // Coarse "pools" override: a hand-edit to any pool line suppresses
      // notes-driven DROPS for the session (adds are still allowed). A line's
      // Marbletech checkbox is also a pool_lines field — treat it as a
      // marbletech override too so notes won't reset a hand-toggled finish.
      manualOverrides.add('pools')
      if (t.name.includes('[marbletech]')) manualOverrides.add('marbletech')
    }
    if (t.id === 'sales-call-booked-at') {
      // A manual set OR clear (the input's native "x") both land here and
      // both clear any pending extraction error, per spec.
      manualOverrides.add('sales_call')
      setSalesCallError('')
      salesCallAutoRetries = 0   // a fresh edit re-arms the bounded auto-retry
      scheduleSalesCallPush()
    }
  }
  if (t.id === 'state-select') {
    // Clearing site 3 of 4 (ADR-0013): the candidates belong to the old
    // state's lookup and the override to the old customer context — a state
    // flip drops both before anything recalculates.
    clearOriginCandidates()
    clearOriginOverride()
    renderDepotCaption()
    renderInstallerOptions()
  }
  if (t.id === 'state-select' || t.name?.startsWith('pool_lines')) refreshForcedOrigin()
  // A staff gesture on the Depot select pins the distance origin to that
  // depot and re-routes from it. Picking a depot supersedes any hand-typed
  // distance (manual distance wins only UNTIL the depot changes); back to
  // Auto reverts to nearest-depot on the next lookup. No isTrusted guard
  // (unlike #distance-input): our own writes go through setDepotSelect, which
  // NEVER dispatches change, so any change event here is a staff action —
  // and Playwright's select_option (system tests) is untrusted by design.
  if (t.id === 'depot-select') {
    depotManuallySelected = t.value !== ''
    distanceManuallyEdited = false
    renderDepotCaption()
    if (currentContact) lookupDistance()
  }
  // Changing manufacturer with a contact selected recomputes distance from the
  // new manufacturer's depots. Any depot override belongs to the OLD
  // manufacturer's depot list — drop it rather than re-sending a stale id.
  if (t.id === 'manufacturer-select') {
    setDepotSelect(null)
    depotManuallySelected = false
    if (currentContact) lookupDistance()
  }
  if (t.name === 'call_direction') { callAutoRetries = 0; scheduleCallSave(300) }
})

document.addEventListener('input', e => {
  // autoCalculate gates on hasCalculated (re-runs after a first manual Calculate).
  // maybeAutoPrice gates on pool+state+distance all present — covers the notes-
  // driven path where the pool was auto-selected but the user enters distance
  // manually (before any manual Calculate has ever been clicked).
  if (e.target.id === 'distance-input') {
    // Only genuine user keystrokes (isTrusted) count as a manual override —
    // the programmatic re-applies above dispatch synthetic input events.
    // Clearing site 1 of 4 (ADR-0013): a hand-typed distance puts the quote on
    // the manual path, so the geocoded comparison candidates are dropped (the
    // origin override, if any, deliberately survives — manual-edits-win).
    if (e.isTrusted) { distanceManuallyEdited = true; clearOriginCandidates() }
    autoCalculate(); maybeAutoPrice(); renderDepotCaption()
  }
  if (e.target.id === 'call-notes')    { callAutoRetries = 0; scheduleCallSave(); scheduleNotesParse() }
})

// ── Calculate button — event delegation ───────────────────────────────────────

document.addEventListener('click', e => {
  if (e.target.closest('#clear-btn'))           clearAll()
  if (e.target.closest('#calculate-btn'))       submitCalculation()
  if (e.target.closest('#generate-email-btn'))  generateEmail()
  if (e.target.closest('#create-outlook-draft-btn')) createOutlookDraft()
  // Origin flip buttons live inside the Turbo-replaced results frame, so the
  // listener is delegated here (ADR-0013). The button's value is the override
  // ('cranbourne' / 'sa_direct' / '' = back to auto); the server re-chooses
  // and re-renders the frame on the resubmit.
  const flip = e.target.closest('[data-origin-flip]')
  if (flip) {
    setOriginOverride(flip.dataset.originFlip || '')
    submitCalculation()
  }
})

// ── Clear / reset everything ────────────────────────────────────────────────────
// Resets the price builder to defaults, clears the rendered price, the email draft,
// and any selected HubSpot contact.

function clearAll() {
  // Price builder fields → defaults
  const mfg = document.getElementById('manufacturer-select')
  if (mfg) mfg.selectedIndex = 0
  const state = document.getElementById('state-select')
  if (state) state.selectedIndex = 0
  const dist = document.getElementById('distance-input')
  if (dist) dist.value = ''
  // Depot → Auto (explicit like the installer below — clearContactDistance
  // also resets it, but this must not depend on that ordering).
  setDepotSelect(null)
  depotManuallySelected = false
  renderDepotCaption()
  // Clearing site 4 of 4 (ADR-0013), explicit for the same ordering reason:
  // the comparison candidates + origin override reset with the form.
  clearOriginCandidates()
  clearOriginOverride()
  clearContactDistance()
  const solo = document.getElementById('dt-solo')
  if (solo) solo.checked = true

  // Pool lines → single empty line, default manufacturer (Vue component)
  enqueue('reset', null)
  // The bridge drains on the next tick — refresh the forced-origin hint after.
  setTimeout(refreshForcedOrigin, 0)

  // Price results → placeholder
  const frame = document.getElementById('results')
  if (frame) {
    frame.innerHTML =
      '<div class="card bg-base-100 shadow-md border border-base-300">' +
        '<div class="card-body">' +
          '<h2 class="card-title text-primary">Price Summary</h2>' +
          '<p class="text-base-content/40 text-sm italic mt-2">' +
            'Complete the form and click Calculate.' +
          '</p>' +
        '</div>' +
      '</div>'
  }

  // Installer → "No installer", re-ordered for the default state (explicit
  // like renderDepotCaption above — the state reset fires no change event)
  const installerSel = document.getElementById('email-installer-select')
  if (installerSel) installerSel.value = ''
  renderInstallerOptions()

  // Email draft → cleared (the TipTap editor empties via the bridge)
  clearEmail()
  draftCreated = false
  currentEmailSubject = null
  const emailStatus = document.getElementById('email-status')
  if (emailStatus) emailStatus.textContent =
    'Calculate a price, then generate an email draft to create in Outlook.'

  // Call notes → cleared, session ended (the saved HubSpot call is left as-is).
  // Explicit because setContact(null) below only resets when a contact was selected.
  clearTimeout(callSaveTimer)
  callSession = { contactId: null, callId: null, lastSaved: '', lastDirection: 'OUTBOUND' }
  callDirty = false
  callRetry = false
  const callTa = document.getElementById('call-notes')
  if (callTa) callTa.value = ''
  const callOutbound = document.getElementById('call-dir-outbound')
  if (callOutbound) callOutbound.checked = true
  setCallStatus('')

  // Sales Call Booked → cleared (the HubSpot property is left as-is, matching
  // the Call Notes text reset above — this is a form reset, not a retraction).
  resetSalesCallField()

  // Selected HubSpot contact → cleared (also refreshes the email section state)
  setContact(null)

  // Call Notes → Pricing reconciliation state reset: notes resume full control.
  clearTimeout(notesParseTimer)
  manualOverrides.clear()
  notesPoolKeys = []

  hasCalculated = false
}

// ── Contact (HubSpot) ─────────────────────────────────────────────────────────

let currentContact = null
let currentEmailSubject = null

// True once the current body has been pushed to Outlook as a draft; drives the
// persistent "Draft created ✓" affordance. Cleared the moment the draft goes
// stale — the body is edited (watcher below), or the contact / form is reset.
let draftCreated = false

// Any edit to the composed body after a draft was created makes that draft stale,
// so drop the ✓ and let the button return to "Create Outlook draft". Seeding a
// fresh body (Generate Email) also lands here, which is correct: a regenerated
// body needs a new draft.
watch(emailHtml, () => {
  if (!draftCreated) return
  draftCreated = false
  updateEmailSection()
})

function updateEmailSection() {
  const hasBody = emailReady.value

  // Outlook draft is gated on a connected mailbox + body text only — a contact
  // (recipient) is optional, so an empty To is allowed.
  const outlookBtn = document.getElementById('create-outlook-draft-btn')
  if (outlookBtn) {
    if (draftCreated) {
      // Persistent success state until the draft goes stale (body/contact/reset).
      outlookBtn.disabled = true
      outlookBtn.textContent = 'Draft created ✓'
      outlookBtn.classList.add('btn-success')
      outlookBtn.classList.remove('btn-primary')
    } else {
      const connected = document.getElementById('email-section')?.dataset.outlookConnected === 'true'
      outlookBtn.disabled = !(connected && hasBody)
      outlookBtn.textContent = 'Create Outlook draft'
      outlookBtn.classList.add('btn-primary')
      outlookBtn.classList.remove('btn-success')
    }
  }
}

// ── Call Notes (auto-saved to HubSpot as one call engagement per session) ─────
// A session spans one contact selection: the first save creates the call,
// later saves PATCH it. Clearing/switching the contact ends the session.

let callSession   = { contactId: null, callId: null, lastSaved: '', lastDirection: 'OUTBOUND' }
let callSaveTimer = null
let callInFlight  = null   // Promise of the save in progress — serialises saves
let callDirty     = false  // input arrived while a save was in flight
let callRetry     = false  // last save failed; force a retry on the next tick

// ── HubSpot save failure policy (shared by call-notes + sales-call-booked) ──
// A permanent (4xx) HubSpot failure can never succeed by resending the same
// payload, so it must NOT auto-retry — on 2026-08-06 a call log deleted in
// HubSpot mid-session turned the old unconditional retry into 4,326 404s over
// ~5 hours. Transient failures (network, 429, 5xx) get a bounded exponential
// backoff; after that, only a fresh user edit re-arms the save.
const SAVE_MAX_AUTO_RETRIES = 5
const saveRetryDelay = attempt => Math.min(2000 * 2 ** (attempt - 1), 30000)
let callAutoRetries      = 0
let salesCallAutoRetries = 0

// 'permanent' | 'transient'. hubspotStatus is HubSpot's own status relayed by
// the backend (null when the backend failed before/without a HubSpot response
// — e.g. a timeout reaching HubSpot — which is transient). With no HubSpot
// status, our controller's 422 wraps such a backend-side failure (transient),
// while any other 4xx (401 session expired, 403 CSRF) won't heal on its own.
function classifySaveError(err) {
  const s = err.hubspotStatus
  if (typeof s === 'number') {
    if (s === 429 || s >= 500) return 'transient'
    if (s >= 400) return 'permanent'
  }
  const h = err.httpStatus
  if (typeof h === 'number' && h >= 400 && h < 500) return h === 422 ? 'transient' : 'permanent'
  return 'transient'
}

// Enrich fetch failures with both statuses so classifySaveError can rule.
// Tolerates a non-JSON body (proxy/HTML error page): classify by HTTP status.
async function throwSaveError(r) {
  let data = {}
  try { data = await r.json() } catch { /* non-JSON body */ }
  if (!r.ok || data.error) {
    const err = new Error(data.error || `Save failed (${r.status})`)
    err.httpStatus = r.status
    err.hubspotStatus = typeof data.hubspot_status === 'number' ? data.hubspot_status : null
    throw err
  }
  return data
}

function callDirection() {
  return document.getElementById('call-dir-inbound')?.checked ? 'INBOUND' : 'OUTBOUND'
}

function setCallStatus(text, tone) {
  const el = document.getElementById('call-status')
  if (!el) return
  el.textContent = text
  el.className = 'text-xs min-h-4 ' +
    (tone === 'ok' ? 'text-success' : tone === 'err' ? 'text-error' : 'text-base-content/40')
}

function scheduleCallSave(delay = 2000) {
  clearTimeout(callSaveTimer)
  callSaveTimer = setTimeout(flushCallSave, delay)
}

async function flushCallSave() {
  if (callInFlight) { callDirty = true; return }
  if (!callSession.contactId) return  // no contact yet — text buffers in the textarea
  const ta = document.getElementById('call-notes')
  if (!ta) return

  const text      = ta.value
  const direction = callDirection()
  // Never create a call for whitespace-only text; emptying an existing call PATCHes it.
  if (!callSession.callId && text.trim() === '') return
  if (!callRetry && text === callSession.lastSaved && direction === callSession.lastDirection) return

  // Capture the target before any await: a contact cleared/switched mid-flight
  // must complete against the old call, never attach to the new contact.
  const target = { contactId: callSession.contactId, callId: callSession.callId, text, direction }
  setCallStatus('Saving…')
  callInFlight = saveCall(target)
  try {
    const data = await callInFlight
    if (callSession.contactId === target.contactId) {
      if (!target.callId) callSession.callId = data.id
      callSession.lastSaved     = target.text
      callSession.lastDirection = target.direction
      setCallStatus('Saved', 'ok')
    }
    callRetry = false
    callAutoRetries = 0
  } catch (err) {
    console.error('[CallNotes] save failed:', err)
    callRetry = true
    if (target.callId && err.hubspotStatus === 404 && callSession.contactId === target.contactId) {
      // The logged call was deleted in HubSpot (e.g. a rep tidying up). The
      // notes are still in the textarea — forget the dead id and recreate the
      // call with the full current text. No tight loop is possible: each 404
      // consumes the stored id, so continuing costs a create+update round trip
      // and only happens while the user keeps editing.
      callSession.callId    = null
      callSession.lastSaved = ''
      setCallStatus('Call log was deleted in HubSpot — recreating…', 'err')
      scheduleCallSave(200)
    } else if (classifySaveError(err) === 'permanent') {
      // Resending the same payload can never succeed — wait for a fresh edit.
      setCallStatus('Not saved — HubSpot rejected it. Edit the notes to retry.', 'err')
    } else if (callAutoRetries < SAVE_MAX_AUTO_RETRIES) {
      callAutoRetries += 1
      setCallStatus('Not saved — retrying…', 'err')
      scheduleCallSave(saveRetryDelay(callAutoRetries))
    } else {
      setCallStatus('Not saved — HubSpot unreachable. Edit the notes to retry.', 'err')
    }
  } finally {
    callInFlight = null
    if (callDirty) { callDirty = false; scheduleCallSave(200) }
  }
}

async function saveCall({ contactId, callId, text, direction }) {
  const fd = new FormData()
  if (callId) fd.append('call_id', callId)
  else        fd.append('contact_id', contactId)
  fd.append('body_text', text)
  fd.append('direction', direction)
  const r = await fetch(callId ? '/hubspot/update_call' : '/hubspot/create_call', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken() },
    body: fd
  })
  return throwSaveError(r)
}

// Called from setContact: ends the session when the contact changes/clears and
// starts a fresh one for a new contact (flushing any pre-selection buffered text).
function resetCallSession(contact) {
  const next = contact?.id ?? null
  if (next === callSession.contactId) return  // same contact re-selected — keep the session

  clearTimeout(callSaveTimer)
  const hadContact = callSession.contactId !== null
  callSession = { contactId: next, callId: null, lastSaved: '', lastDirection: 'OUTBOUND' }
  callDirty = false
  callRetry = false
  callAutoRetries = 0

  const ta = document.getElementById('call-notes')
  if (hadContact) {
    // Switching away or clearing: the old call keeps its saved notes; field resets.
    if (ta) ta.value = ''
    const outbound = document.getElementById('call-dir-outbound')
    if (outbound) outbound.checked = true
    setCallStatus('')

    // Sales Call Booked must be cleared here too, not left to be "(re-)pushed"
    // to whichever contact is now selected: leaving a stale value in the field
    // across a switch silently attributed contact A's booked-call time to
    // contact B on the very next debounce (data-integrity bug, fixed
    // 2026-07-16 — see the sales-call-booked-from-notes review). The already-
    // pushed value on the OLD contact's HubSpot record is untouched — this
    // only clears the LOCAL field so nothing leaks onto the new contact.
    resetSalesCallField()
  } else if (next && ta && ta.value.trim() !== '') {
    // Text buffered before a contact was selected — save it to the new session soon.
    scheduleCallSave(300)
  }

  if (!hadContact) {
    // A Sales Call Booked value set/typed before any contact was selected is
    // buffered the same way as Call Notes text — push it once a contact is
    // now available. (When hadContact is true, resetSalesCallField() above
    // already cleared everything — nothing to push.)
    salesCallLastPushed = { contactId: null, value: undefined }
    if (next && salesCallIso()) scheduleSalesCallPush(300)
  }
}

// ── Sales Call Booked (extracted from Call Notes or set manually; pushed to
// HubSpot's "Sales Call Booked Date and Time" contact property) ───────────────
// Assumed/displayed in Australia/Brisbane (GMT+10, no daylight saving) —
// CFPK's operations are QLD-based (matches SalesCallExtractor server-side).
// The datetime-local input therefore carries a Brisbane WALL-CLOCK value with
// no timezone of its own: BRISBANE_OFFSET is appended when building the ISO
// string sent to the server, and a server ISO value (already Brisbane time)
// is applied to the input by slicing off its offset rather than round-
// tripping through Date(), so the browser's own timezone can never shift the
// displayed wall-clock value.

const BRISBANE_OFFSET = '+10:00'

function salesCallInput() {
  return document.getElementById('sales-call-booked-at')
}

function setSalesCallError(text) {
  const el = document.getElementById('sales-call-booked-error')
  if (el) el.textContent = text || ''
}

// The input's current value as a full ISO8601 string (Brisbane offset), or
// null when blank.
function salesCallIso() {
  const v = salesCallInput()?.value
  return v ? `${v}:00${BRISBANE_OFFSET}` : null
}

function setSalesCallFromIso(iso) {
  const input = salesCallInput()
  if (input) input.value = iso.slice(0, 16) // "YYYY-MM-DDTHH:MM", offset dropped
}

let salesCallSaveTimer  = null
let salesCallInFlight   = null   // Promise of the push in progress — serialises pushes
let salesCallDirty      = false  // a change arrived while a push was in flight
let salesCallLastPushed = { contactId: null, value: undefined }

// Clears the field, any pending/error/dirty state, and the push-dedup memory —
// but schedules NO push. Used by clearAll() (a form reset) and by
// resetCallSession() on a contact switch (the field must never carry a stale
// value onto whichever contact is selected next — see the cross-contamination
// fix note at its call site). The already-pushed value on HubSpot is left as-is
// in both cases; this only ever clears the LOCAL field.
function resetSalesCallField() {
  clearTimeout(salesCallSaveTimer)
  const el = salesCallInput()
  if (el) el.value = ''
  setSalesCallError('')
  salesCallDirty = false
  salesCallAutoRetries = 0
  salesCallLastPushed = { contactId: null, value: undefined }
  manualOverrides.delete('sales_call')
  notesSalesCallSignal = null
}

function scheduleSalesCallPush(delay = 500) {
  clearTimeout(salesCallSaveTimer)
  salesCallSaveTimer = setTimeout(flushSalesCallPush, delay)
}

async function flushSalesCallPush() {
  if (salesCallInFlight) { salesCallDirty = true; return }
  if (!currentContact) return   // nothing to patch until a contact is selected

  const value = salesCallIso()
  const contactId = currentContact.id
  if (salesCallLastPushed.contactId === contactId && salesCallLastPushed.value === value) return

  const target = { contactId, value }
  salesCallInFlight = pushSalesCall(target)
  try {
    await salesCallInFlight
    salesCallLastPushed = target
    salesCallAutoRetries = 0
  } catch (err) {
    console.error('[SalesCallBooked] push failed:', err)
    if (classifySaveError(err) === 'permanent') {
      // Resending the same value can never succeed — surface it and wait for
      // the booking (or contact) to change, which schedules a fresh push.
      setSalesCallError("Couldn't save the booking to HubSpot — change it to retry")
    } else if (salesCallAutoRetries < SAVE_MAX_AUTO_RETRIES) {
      salesCallAutoRetries += 1
      scheduleSalesCallPush(saveRetryDelay(salesCallAutoRetries))
    } else {
      setSalesCallError("Couldn't save the booking to HubSpot — change it to retry")
    }
  } finally {
    salesCallInFlight = null
    if (salesCallDirty) { salesCallDirty = false; scheduleSalesCallPush(200) }
  }
}

async function pushSalesCall({ contactId, value }) {
  const fd = new FormData()
  fd.append('contact_id', contactId)
  fd.append('sales_call_booked_at', value || '')
  const r = await fetch('/hubspot/update_sales_call_booked', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken() },
    body: fd
  })
  return throwSaveError(r)
}

// ── Call Notes → Pricing fields (deterministic, natural-language successor to QuickSet) ──
// The SAME #call-notes input that schedules the HubSpot call-save also drives the
// Pricing fields: on a separate debounce we POST the prose to /parse_notes and
// reconcile the returned notes-controlled fields onto the form. The call-save
// (scheduleCallSave) is left entirely untouched — both run off the input event.
//
// State and distance are NEVER touched here: they come from the contact/geocode
// flow (lookupDistance) and manual entry. The endpoint doesn't even emit them.
//
// Reconciliation principle: each notes-controlled field (delivery, marbletech,
// pool lines) is either notes-owned or manual-owned. A notes mention (re)claims
// the field for the notes; a hand-edit claims it for the user until the notes
// mention it again. See the per-field decision table in the spec's plan.md.

let notesParseTimer = null
let parseSeq        = 0          // monotonic guard — a slow parse can't apply over a newer one
let notesApplying   = false      // true while applyNotesResult writes fields, so its own
                                 // programmatic edits don't register as manual overrides
const manualOverrides = new Set() // 'delivery' | 'marbletech' | 'pools' | 'sales_call'
let notesPoolKeys     = []        // pool_keys the previous apply derived from the notes
// The last sales-call signal the NOTES resolved to — `resolved:<iso>`,
// 'ambiguous', or null (no trigger). /parse_notes re-parses the FULL notes
// text every debounce, so a still-present trigger phrase re-resolves to the
// SAME signal on every tick; only a change in this signal counts as "the
// notes mention it again" (a manual clear must otherwise survive the notes
// going quiet or re-affirming what they already said — see applyNotesResult).
let notesSalesCallSignal = null

function scheduleNotesParse() {
  clearTimeout(notesParseTimer)
  notesParseTimer = setTimeout(runNotesParse, 600)
}

async function runNotesParse() {
  const ta = document.getElementById('call-notes')
  if (!ta) return
  const notes = ta.value
  const seq = ++parseSeq

  let result
  try {
    const r = await fetch('/parse_notes', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify({ notes })
    })
    if (!r.ok) return            // fail-quiet: wait for the next debounce
    result = await r.json()
  } catch {
    return                       // network error → no change this tick
  }

  if (seq !== parseSeq) return   // a newer parse superseded this one
  applyNotesResult(result)
}

// Maps a /parse_notes result onto the form, honouring manual-edits-win.
// Pool lines go through the Vue bridge (enqueue); delivery/marbletech are set
// via the existing controls. Never writes #distance-input or #state-select.
function applyNotesResult(R) {
  notesApplying = true
  try {
    // ── Manufacturer (sets the pool option set; only when the notes name one) ──
    if (R.manufacturer) {
      const mfg = document.getElementById('manufacturer-select')
      if (mfg && mfg.value !== R.manufacturer) {
        // Only switch if the notes-named manufacturer is a real option.
        const has = Array.from(mfg.options).some(o => o.value === R.manufacturer)
        if (has) {
          mfg.value = R.manufacturer
          enqueue('setManufacturer', R.manufacturer)
        }
      }
    }

    // ── Delivery type ──
    // non-null → set radio + clear override (notes reclaim); null + manual → leave;
    // null + not manual → revert to default (solo).
    if (R.delivery_type) {
      setDeliveryRadio(R.delivery_type)
      manualOverrides.delete('delivery')
    } else if (!manualOverrides.has('delivery')) {
      setDeliveryRadio('solo')
    }

    // ── Pool lines ──
    const notesKeysNow = (R.pool_lines || []).map(l => l.pool_key)
    reconcilePoolLines(notesKeysNow)

    // ── Marbletech (all-lines flag) ──
    // token present → apply to all notes-owned lines + clear override; absent +
    // manual → leave; absent + not manual → revert non-manual lines to off.
    if (R.marbletech_all !== null && R.marbletech_all !== undefined) {
      enqueue('setMarbletechAll', !!R.marbletech_all)
      manualOverrides.delete('marbletech')
    } else if (!manualOverrides.has('marbletech')) {
      enqueue('setMarbletechAll', false)
    }

    // ── Sales call booked ──
    // resolved → set field + clear override (a mention reclaims it) + clear
    // error + push; ambiguous → clear override (still a reclaim attempt — the
    // NEXT resolving tick must be able to overwrite) + persistent error, value
    // left untouched; silent (no trigger at all) → clear error only. Unlike
    // delivery/marbletech there is no "revert to a default" case: a
    // previously-resolved value is never silently nulled out by the notes
    // going quiet (per spec) — only a manual clear or a new mention changes it.
    //
    // /parse_notes re-parses the FULL current notes text every debounce tick
    // (not just deltas), and notes are append-only, so a still-present trigger
    // phrase resolves to the exact same signal on every tick. That must NOT
    // count as "the notes mention it again" — a manual clear must survive the
    // notes going quiet OR simply re-affirming what they already said; only a
    // signal that DIFFERS from the last one the notes resolved to is a genuine
    // new mention, and only a new mention reclaims a manually-owned field
    // (fixed 2026-07-16 — a manual clear was silently un-clearing itself on
    // the next keystroke elsewhere in the notes; see the review).
    const salesCallSignal = R.sales_call_booked_at
      ? `resolved:${R.sales_call_booked_at}`
      : (R.sales_call_ambiguous ? 'ambiguous' : null)
    const salesCallIsNewMention = salesCallSignal !== null && salesCallSignal !== notesSalesCallSignal
    notesSalesCallSignal = salesCallSignal

    if (salesCallIsNewMention || !manualOverrides.has('sales_call')) {
      if (R.sales_call_booked_at) {
        setSalesCallFromIso(R.sales_call_booked_at)
        manualOverrides.delete('sales_call')
        setSalesCallError('')
        scheduleSalesCallPush()
      } else if (R.sales_call_ambiguous) {
        manualOverrides.delete('sales_call')
        setSalesCallError("Couldn't work out the sales call date/time from the notes — set it manually or fix the notes")
      } else {
        setSalesCallError('')
      }
    }
  } finally {
    notesApplying = false
  }

  // From the debounce tail — after enqueued bridge commands have a tick to
  // drain into the DOM: refresh the forced-origin surfaces first (notes-driven
  // pool changes bypass DOM change events) so a cached lookup re-applies with
  // the right number/unit, THEN auto-price off that corrected distance.
  setTimeout(() => { refreshForcedOrigin(); maybeAutoPrice() }, 0)
}

function setDeliveryRadio(value) {
  const id  = value === 'multi' ? 'dt-multi' : 'dt-solo'
  const el  = document.getElementById(id)
  if (el && !el.checked) {
    el.checked = true
    el.dispatchEvent(new Event('change', { bubbles: true }))
  }
}

// Reconcile the form's pool lines against the notes' current pool keys.
// Drop notes-owned lines whose phrase vanished (unless a hand-edit suppressed
// drops via the 'pools' override); add any new notes keys; keep everything else.
function reconcilePoolLines(notesKeysNow) {
  const currentKeys = Array.from(
    document.querySelectorAll('[name^="pool_lines"][name$="[pool_key]"]')
  ).map(el => el.value).filter(Boolean)

  // Drops: previously notes-owned + now absent from notes + drops not suppressed.
  if (!manualOverrides.has('pools')) {
    notesPoolKeys.forEach(key => {
      if (!notesKeysNow.includes(key) && currentKeys.includes(key)) {
        enqueue('removeLine', key)
      }
    })
  }

  // Adds: notes keys not already present as a line.
  notesKeysNow.forEach(key => {
    if (!currentKeys.includes(key)) enqueue('setPool', key)
  })

  notesPoolKeys = notesKeysNow
}

// Auto-price gate: only when ≥1 pool line + a state + a finite distance > 0 are
// present. State/distance come from the contact/geocode flow — never set here.
function maybeAutoPrice() {
  const hasPool = Array.from(
    document.querySelectorAll('[name^="pool_lines"][name$="[pool_key]"]')
  ).some(el => el.value)
  if (!hasPool) return

  const state = document.getElementById('state-select')?.value || ''
  if (!state) return

  const dist = parseFloat(document.getElementById('distance-input')?.value || '')
  if (!Number.isFinite(dist) || dist <= 0) return

  clearTimeout(calcTimer)
  calcTimer = setTimeout(submitCalculation, 200)
}

// ── Installer dropdown ────────────────────────────────────────────────────────
// Rebuilds the Email Draft installer <select> from the data embedded on the
// element, ordering the currently-selected delivery state's installers first.
// Re-runs whenever the delivery state changes. The chosen installer's name and
// phone ride in the option's dataset, so the email draft reads them straight off
// the selection — no separate phone field. Preserves the current pick across a
// re-order when that installer is still in the list.
function renderInstallerOptions() {
  const sel = document.getElementById('email-installer-select')
  if (!sel) return

  let installers = []
  try { installers = JSON.parse(sel.dataset.installers || '[]') } catch { installers = [] }

  const state    = document.getElementById('state-select')?.value || ''
  const prevName  = sel.selectedOptions[0]?.dataset.name  || ''
  const prevPhone = sel.selectedOptions[0]?.dataset.phone || ''

  const options = ['<option value="">No installer</option>']
  orderInstallers(installers, state).forEach((inst, i) => {
    options.push(
      `<option value="${i}" data-name="${escapeHtml(inst.name)}" data-phone="${escapeHtml(inst.phone || '')}">` +
      `${escapeHtml(installerLabel(inst))}</option>`
    )
  })
  sel.innerHTML = options.join('')

  if (prevName || prevPhone) {
    const keep = Array.from(sel.options).find(
      o => o.dataset.name === prevName && o.dataset.phone === prevPhone)
    if (keep) sel.value = keep.value
  }
}

async function generateEmail() {
  const status = document.getElementById('email-status')
  if (status) status.textContent = 'Generating…'

  const fd = new FormData()
  fd.append('manufacturer',      document.getElementById('manufacturer-select')?.value || '')
  fd.append('state',             document.getElementById('state-select')?.value || '')
  fd.append('distance_or_hours', document.getElementById('distance-input')?.value || '0')
  appendOriginParams(fd)
  const dt = document.querySelector('input[name="delivery_type_display"]:checked')
  fd.append('delivery_type', dt?.value || 'solo')
  document.querySelectorAll('[name^="pool_lines"]').forEach(el => {
    fd.append(el.name, el.type === 'checkbox' ? (el.checked ? '1' : '0') : el.value)
  })
  if (currentContact) fd.append('contact_id', currentContact.id)
  // Compose options that drive the HTML email's option-driven blocks.
  fd.append('shell_only', document.getElementById('email-shell-only')?.checked ? '1' : '0')
  const installerOpt = document.getElementById('email-installer-select')?.selectedOptions[0]
  fd.append('installer_name',  installerOpt?.dataset.name  || '')
  fd.append('installer_phone', installerOpt?.dataset.phone || '')
  // Sales Call Booked's current value is the single source of truth for the
  // {{sales_call_booked}} email pill — never a fresh notes re-parse.
  fd.append('sales_call_booked_at', salesCallIso() || '')

  try {
    const r = await fetch('/email_draft', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
      body: fd
    })
    const data = await r.json()
    if (data.error) {
      if (status) status.textContent = `Error: ${data.error}`
      return
    }
    // data.content is the editable email BODY (semantic HTML). Seed the TipTap
    // editor; the fixed shell + signature are added server-side on send.
    seedEmail(data.content)
    currentEmailSubject = data.subject || null
    if (status) status.textContent = currentContact
      ? `Draft ready for ${currentContact.firstname} ${currentContact.lastname}`
      : 'Draft ready (no contact selected)'
    updateEmailSection()
  } catch (err) {
    if (status) status.textContent = `Error: ${err.message}`
  }
}

// Creates a draft in the fixed "Nimbus Post Call Drafts" folder in the signed-in
// user's Outlook mailbox (created server-side if missing). A recipient is optional
// (empty To allowed); only a connected mailbox + body text are required (enforced
// by updateEmailSection).
async function createOutlookDraft() {
  const btn = document.getElementById('create-outlook-draft-btn')
  if (!btn || btn.disabled) return

  const statusEl = document.getElementById('email-status')
  const bodyText = emailHtml.value || ''   // the staff-edited body; server sanitises + wraps it
  const subject  = currentEmailSubject || 'Your DIY Pool Options & Next Steps - Clear, Easy, Tailored for You'

  btn.disabled = true
  btn.textContent = 'Creating…'

  try {
    const r = await fetch('/outlook/create_draft', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify({
        subject,
        body: bodyText,
        to: currentContact?.email || '',
        content_type: 'HTML'
      })
    })
    const data = await r.json()

    if (data.reconnect) {
      // Token expired/revoked — point the user to reconnect; keep the draft text.
      if (statusEl) {
        statusEl.innerHTML =
          'Your Outlook connection has expired. ' +
          '<a href="/account" class="link link-primary font-medium">Reconnect Outlook →</a>'
      }
      return
    }
    if (data.error === 'not_connected') {
      if (statusEl) {
        statusEl.innerHTML =
          'Connect Outlook first. ' +
          '<a href="/account" class="link link-primary font-medium">Connect Outlook →</a>'
      }
      return
    }
    if (data.error) throw new Error(data.error)

    if (statusEl) statusEl.textContent = 'Draft created in your Outlook.'
    draftCreated = true
  } catch (err) {
    if (statusEl) statusEl.textContent = `Outlook error: ${err.message}`
  } finally {
    // Render the button from state: "Draft created ✓" on success, or back to
    // "Create Outlook draft" (enabled) on any failure so the user can retry.
    updateEmailSection()
  }
}

const STATE_MAP = {
  'queensland': 'QLD', 'qld': 'QLD',
  'new south wales': 'NSW', 'nsw': 'NSW',
  'victoria': 'VIC', 'vic': 'VIC',
  'south australia': 'SA', 'sa': 'SA',
  'western australia': 'WA', 'wa': 'WA',
  'australian capital territory': 'ACT', 'act': 'ACT'
}

function mapState(raw) {
  if (!raw) return null
  const key = String(raw).trim().toLowerCase().replace(/\.+$/, '').trim().replace(/\s+/g, ' ')
  return STATE_MAP[key] ?? null
}

// Sets the State select from the contact's address state (overwrite is intentional).
// Leaves the select untouched when the state can't be mapped. Returns {text, tone}
// feedback describing the outcome for the caller to surface.
function applyContactState(contact) {
  // CFPK carries the state in the custom `state_territory` field; the standard
  // `state` is a fallback for contacts that only have that one populated.
  const raw = contact.state_territory || contact.state
  const mapped = mapState(raw)
  if (!mapped) {
    return raw
      ? { text: `Couldn't read state "${raw}" from contact`, tone: 'err' }
      : { text: 'No state on contact address', tone: 'err' }
  }
  const sel = document.getElementById('state-select')
  if (sel) {
    sel.value = mapped
    // Programmatic .value doesn't emit change — dispatch so the delegated
    // listener triggers the debounced auto-recalculate.
    sel.dispatchEvent(new Event('change', { bubbles: true }))
  }
  return { text: `Set state: ${mapped} from contact address`, tone: 'ok' }
}

// Move keyboard focus to the Call Notes field (cursor at end of any existing
// text) and scroll it into view. Called at the explicit contact-selection sites
// — NOT inside setContact, which early-returns on re-selecting the same contact
// (we want focus on re-select too) and runs on unrelated re-renders that must
// not steal focus. No-ops on a page without #call-notes.
function focusCallNotes() {
  const ta = document.getElementById('call-notes')
  if (!ta) return
  ta.focus()
  const len = ta.value.length
  ta.setSelectionRange?.(len, len)      // cursor at end of existing text
  ta.scrollIntoView({ block: 'center', behavior: 'smooth' })
}

function setContact(contact) {
  currentContact = contact
  // A different (or cleared) recipient makes any prior draft stale.
  draftCreated = false
  resetCallSession(contact)
  const selectedDiv   = document.getElementById('contact-selected')
  const selectedLabel = document.getElementById('contact-selected-label')
  const inputWrap     = document.getElementById('contact-input-wrap')
  const details       = document.getElementById('contact-details')

  if (contact) {
    const parts = [contact.firstname, contact.lastname].filter(Boolean).join(' ')
    const label = contact.email ? `${parts} (${contact.email})` : parts
    if (selectedLabel) {
      // Link the name straight to the HubSpot contact record so staff don't
      // re-search there. Falls back to plain text if we lack the portal/id.
      const portal = selectedDiv?.dataset.hubspotPortal
      if (portal && contact.id) {
        const url = `https://app.hubspot.com/contacts/${portal}/record/0-1/${encodeURIComponent(contact.id)}`
        selectedLabel.innerHTML =
          `<a href="${url}" target="_blank" rel="noopener noreferrer" class="hover:underline">${escapeHtml(label)}</a>`
      } else {
        selectedLabel.textContent = label
      }
    }
    if (details) {
      const st = contact.state_territory || contact.state
      const locality = [contact.suburb, contact.city, [st, contact.zip].filter(Boolean).join(' ')]
        .filter(Boolean).join(', ')
      const phone = contact.mobilephone || contact.phone || contact.secondary_mobile_phone_number
      const rows = [phone, contact.address, locality].filter(Boolean)
      details.innerHTML = rows.map(r => `<div class="truncate">${escapeHtml(r)}</div>`).join('')
      details.classList.toggle('hidden', rows.length === 0)
    }
    selectedDiv?.classList.remove('hidden')
    selectedDiv?.classList.add('flex')
    inputWrap?.classList.add('hidden')
    updateEmailSection()
    const stateFeedback = applyContactState(contact)
    // Blank the old persistent distance line until the new lookup resolves, so
    // switching contacts never leaves a stale depot distance pinned.
    clearContactDistance()
    // Auto-populate Distance from the address — sequenced AFTER state is set so
    // the VIC/non-VIC unit decision reads the just-applied state.
    lookupDistance()
    return stateFeedback
  }

  selectedDiv?.classList.add('hidden')
  selectedDiv?.classList.remove('flex')
  if (details) { details.innerHTML = ''; details.classList.add('hidden') }
  clearContactDistance()
  inputWrap?.classList.remove('hidden')
  const inp = document.getElementById('contact-search-input')
  if (inp) inp.value = ''
  updateEmailSection()
  return null
}

let contactSearchTimer = null

function initContactSearch() {
  const input    = document.getElementById('contact-search-input')
  const clearBtn = document.getElementById('contact-clear-btn')
  if (!input) return

  input.addEventListener('input', () => {
    clearTimeout(contactSearchTimer)
    const q = input.value.trim()
    if (q.length < 2) { hideContactDropdown(); return }
    contactSearchTimer = setTimeout(() => fetchContactResults(q), 300)
  })

  input.addEventListener('blur', () => {
    setTimeout(hideContactDropdown, 200)
  })

  clearBtn?.addEventListener('click', () => clearAll())
}

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

async function fetchContactResults(query) {
  showContactLoading()
  try {
    const r = await fetch(`/hubspot/search_contacts?query=${encodeURIComponent(query)}`, {
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': csrfToken() }
    })
    const data = await r.json()
    if (!r.ok || (data && !Array.isArray(data) && data.error)) {
      showContactError(data.error || `Search failed (${r.status})`)
    } else {
      showContactDropdown(Array.isArray(data) ? data : [], query)
    }
  } catch (err) {
    showContactError(`Search error: ${err.message}`)
  }
}

function showContactLoading() {
  const dropdown = document.getElementById('contact-dropdown')
  if (!dropdown) return
  dropdown.innerHTML = '<div class="p-2 text-sm text-base-content/40">Searching…</div>'
  dropdown.classList.remove('hidden')
}

function showContactDropdown(contacts, query = '') {
  const dropdown = document.getElementById('contact-dropdown')
  if (!dropdown) return

  if (!contacts.length) {
    dropdown.innerHTML = `<div class="p-2 text-sm text-base-content/50">No results for "${escapeHtml(query)}"</div>`
  } else {
    dropdown.innerHTML = ''
    contacts.forEach(c => {
      const btn  = document.createElement('button')
      btn.type   = 'button'
      btn.className = 'btn btn-ghost btn-sm w-full justify-start font-normal text-left rounded-none border-0'
      btn.innerHTML = contactRowHtml(c, escapeHtml)
      btn.addEventListener('click', () => {
        const st = setContact(c)
        focusCallNotes()
        if (st) setFeedback(st.text, st.tone)
        hideContactDropdown()
      })
      dropdown.appendChild(btn)
    })
  }
  dropdown.classList.remove('hidden')
}

function showContactError(msg) {
  const dropdown = document.getElementById('contact-dropdown')
  if (!dropdown) return
  dropdown.innerHTML = `<div class="p-2 text-sm text-error">${escapeHtml(msg)}</div>`
  dropdown.classList.remove('hidden')
}

function hideContactDropdown() {
  document.getElementById('contact-dropdown')?.classList.add('hidden')
}

async function searchAndSetContact(query) {
  setFeedback('Searching HubSpot…', 'ok')
  try {
    const r = await fetch(`/hubspot/search_contacts?query=${encodeURIComponent(query)}`, {
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': csrfToken() }
    })
    const data = await r.json()

    if (!r.ok || (data && !Array.isArray(data) && data.error)) {
      setFeedback(`HubSpot: ${data.error || 'search failed'}`, 'err')
      return
    }

    const contacts = Array.isArray(data) ? data : []
    if (!contacts.length) {
      setFeedback(`No HubSpot contacts found for "${query}"`, 'err')
      return
    }
    if (contacts.length === 1) {
      const st = setContact(contacts[0])
      focusCallNotes()
      const name = [contacts[0].firstname, contacts[0].lastname].filter(Boolean).join(' ')
      setFeedback(`Contact → ${name}${st ? ' · ' + st.text : ''}`, st?.tone === 'err' ? 'err' : 'ok')
    } else {
      // Multiple matches — surface the dropdown in the contact control
      const inputWrap = document.getElementById('contact-input-wrap')
      const inp = document.getElementById('contact-search-input')
      document.getElementById('contact-selected')?.classList.add('hidden')
      document.getElementById('contact-selected')?.classList.remove('flex')
      inputWrap?.classList.remove('hidden')
      if (inp) inp.value = query
      showContactDropdown(contacts, query)
      setFeedback(`${contacts.length} matches — select one below`, 'ok')
    }
  } catch (err) {
    setFeedback(`HubSpot error: ${err.message}`, 'err')
  }
  document.getElementById('contact-search-input')?.focus()
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function flash(el) {
  if (!el) return
  el.classList.add('ring-2', 'ring-accent', 'ring-offset-1')
  setTimeout(() => el.classList.remove('ring-2', 'ring-accent', 'ring-offset-1'), 900)
}

function setFeedback(msg, type) {
  const el = document.getElementById('contact-feedback')
  if (!el) return
  el.textContent = msg
  const tone = type === 'err' ? 'text-error'
    : type === 'warn' ? 'text-warning'
      : 'text-success'
  el.className = `text-xs min-h-4 mt-1 ${tone}`
  clearTimeout(el._t)
  el._t = setTimeout(() => {
    el.textContent = ''
    el.className = 'text-xs min-h-4 mt-1 text-base-content/50'
  }, 2500)
}

// ── Distance auto-lookup ────────────────────────────────────────────────────
// Geocodes the selected contact's address and routes to the nearest depot of the
// selected manufacturer, populating the Distance field. The distanceReqSeq
// sequence (declared in the early-state section) guards against stale responses
// (rapid contact/manufacturer changes, or a clear while in flight). On ANY
// failure the Distance field is left untouched — only the feedback line changes.

// Low-confidence wording lives in exactly TWO injected places — the transient
// feedback line and the persistent depot line. Both are named here so the wording
// can't drift (house rule). The reason (when present) is appended in parentheses.
function LOW_CONF_FEEDBACK({ low_confidence_reason } = {}) {
  return `Low-confidence match — verify distance${low_confidence_reason ? ` (${low_confidence_reason})` : ''}`
}
function LOW_CONF_LINE(value, unit, depot, suffix) {
  return `⚠ ~${value} ${unit} from ${depot}${suffix} — verify`
}

// Persistent "distance to depot" line pinned under the contact-details card.
// Unlike the transient feedback line, this stays visible for the whole call.
// The (STATE) suffix is the DEPOT's own state from the lookup response — never
// the customer's pricing state, which once produced lies like "Cranbourne
// Holding Yard (NSW)" — and is omitted when the response predates depot_state.
// On a low-confidence match it renders amber with a ⚠/~/verify marker; a
// confident match renders exactly as before (and clears any stale amber).
function renderContactDistance(data) {
  const el = document.getElementById('contact-distance')
  if (!el) return
  // A forced SA-direct order (ADR-0010) shows the Burton km candidate, not the
  // Cranbourne hours the primary VIC fields carry.
  const { value, depot, unit: u, suffix } = contactDistanceParts(data, forcedSaDirect())
  const { low_confidence } = data
  if (value == null || !depot) return
  if (low_confidence) {
    el.textContent = LOW_CONF_LINE(value, u, depot, suffix)
    el.classList.add('text-warning')
  } else {
    el.textContent = `${value} ${u} from ${depot}${suffix}`
    el.classList.remove('text-warning')
  }
  el.classList.remove('hidden')
}

function clearContactDistance() {
  lastDistanceLookup = null
  lastAppliedForced = null
  // Clearing site 2 of 4 (ADR-0013): the comparison candidates and any staff
  // origin flip belong to the contact being cleared/switched away from.
  clearOriginCandidates()
  clearOriginOverride()
  // Invalidate any in-flight /distance response: a late reply for a contact
  // that was cleared mid-lookup must not resurrect the distance surfaces
  // (or the Depot select) — lookupDistance drops responses whose seq is stale.
  distanceReqSeq++
  // The lookup context is void: any staff depot override belongs to the old
  // contact, so revert the Depot select to Auto (nearest) as well.
  setDepotSelect(null)
  depotManuallySelected = false
  syncDepotSelectDisabled()
  const el = document.getElementById('contact-distance')
  if (el) { el.textContent = ''; el.classList.remove('text-warning'); el.classList.add('hidden') }
}

// Writes the Distance input + feedback + persistent depot line from the cached
// lookup, honouring the CURRENT forced-ness (ADR-0010): forced with a Burton
// candidate → sa_direct km; forced without one → CLEAR the field and surface
// the error (a forced order must never sit on Cranbourne hours); otherwise →
// the primary value exactly as before this spec. The cache is kept either way,
// so un-forcing (dropping the restricted pool) restores the primary value.
function applyDistanceLookup() {
  const data = lastDistanceLookup
  if (!data) return
  const inp = document.getElementById('distance-input')
  const forced = forcedSaDirect()

  // Belt-and-braces for the manual-edits-win contract: a staff-typed figure is
  // only ever replaced by a genuine forced-ness flip or a fresh lookup (which
  // resets the flag below) — never by a same-forcedness re-apply.
  if (distanceManuallyEdited && forced === lastAppliedForced) return

  if (forced && !data.sa_direct) {
    if (inp && inp.value !== '') {
      inp.value = ''
      inp.dispatchEvent(new Event('input', { bubbles: true }))
    }
    const line = document.getElementById('contact-distance')
    if (line) { line.textContent = ''; line.classList.remove('text-warning'); line.classList.add('hidden') }
    setFeedback('No route from Burton SA — this pool ships SA-direct', 'err')
    setDepotSelect(null)
    depotManuallySelected = false
    syncDepotSelectDisabled()
    clearOriginCandidates() // forced orders never compare (ADR-0013)
    lastAppliedForced = forced
    distanceManuallyEdited = false
    return
  }

  const value = forced ? data.sa_direct.value : data.value
  if (inp) {
    inp.value = value
    flash(inp)
    // Programmatic .value doesn't emit input — dispatch so the existing
    // debounced auto-recalculate fires when a result is already showing.
    inp.dispatchEvent(new Event('input', { bubbles: true }))
  }
  if (data.low_confidence) {
    setFeedback(LOW_CONF_FEEDBACK(data), 'warn')
  } else if (forced) {
    setFeedback(`Distance: ${value} km from ${data.sa_direct.depot} (SA-direct)`, 'ok')
  } else if (data.unit === 'hrs' && !data.sa_direct) {
    setFeedback(BURTON_UNROUTABLE_FEEDBACK, 'warn')
  } else {
    setFeedback(data.feedback, 'ok')
  }
  renderContactDistance(data)
  // VIC cheapest-origin candidates (ADR-0013): only a non-forced VIC HOURS
  // apply carries a comparable pair (a VIC order pinned to a km depot, a
  // forced order, or any non-VIC lookup never compares). The Distance field
  // keeps showing the Cranbourne hours; both figures ride the hidden inputs.
  if (!forced && data.unit === 'hrs') {
    setOriginCandidates(data.value, data.sa_direct?.value ?? '')
  } else {
    clearOriginCandidates()
  }
  // Reflect the origin the response actually used in the Depot select. A
  // forced apply snaps it to the SA-direct depot (Burton) and clears any
  // staff override — the forced origin is deterministic (ADR-0010), so the
  // flip is authoritative. Non-forced applies echo data.depot_id, which is
  // either the auto-picked nearest depot or the staff's own override.
  if (forced) {
    setDepotSelect(data.sa_direct.depot_id)
    depotManuallySelected = false
  } else {
    setDepotSelect(data.depot_id)
  }
  syncDepotSelectDisabled()
  renderDepotCaption()
  lastAppliedForced = forced
  distanceManuallyEdited = false
}

async function lookupDistance() {
  if (!currentContact) return

  const manufacturer = document.getElementById('manufacturer-select')?.value || ''
  const region       = document.getElementById('state-select')?.value || '' // mapped state abbrev

  // Send the contact's address as STRUCTURED components (v6 geocoding) rather than
  // a single free-text string, so the matcher can't drift off the street name.
  // In Mapbox v6 an Australian SUBURB is a `locality` feature and `place` means
  // city/town — suburb-as-place made unmatched suburbs fall back to the state
  // centroid (Safety Bay WA → ~1250 km), so each goes in its own component.
  const address_line1 = currentContact.address || ''
  const locality      = currentContact.suburb || ''
  const place         = currentContact.city || ''
  const postcode      = currentContact.zip || ''

  if (!address_line1.trim() && !locality.trim() && !place.trim()) {
    setFeedback('No address on contact — enter distance manually', 'err')
    return
  }

  const seq = ++distanceReqSeq

  // A staff-selected depot pins the distance origin (ADR-0011); on Auto the
  // server picks the nearest depot as always.
  const depotSel = document.getElementById('depot-select')
  const depot_id = depotManuallySelected && depotSel?.value ? depotSel.value : null

  try {
    const r = await fetch('/distance', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify({ address_line1, locality, place, region, postcode, manufacturer, depot_id })
    })
    const data = await r.json()

    if (seq !== distanceReqSeq) return // a newer lookup superseded this one

    if (!r.ok || (data && data.error)) {
      setFeedback(data.error || `Distance lookup failed (${r.status})`, 'err')
      return // leave the Distance field untouched
    }

    lastDistanceLookup = data
    // A fresh lookup legitimately owns the field: clear any manual override
    // so the apply below can never be blocked by a stale flag.
    distanceManuallyEdited = false
    lastAppliedForced = null
    applyDistanceLookup()
  } catch (err) {
    if (seq !== distanceReqSeq) return
    setFeedback(`Distance lookup error: ${err.message}`, 'err')
  }
}

function submitCalculation() {
  const fd = new FormData()
  fd.append('manufacturer',      document.getElementById('manufacturer-select')?.value || '')
  fd.append('state',             document.getElementById('state-select')?.value || '')
  fd.append('distance_or_hours', document.getElementById('distance-input')?.value || '0')
  appendOriginParams(fd)
  const dt = document.querySelector('input[name="delivery_type_display"]:checked')
  fd.append('delivery_type', dt?.value || 'solo')
  document.querySelectorAll('[name^="pool_lines"]').forEach(el => {
    fd.append(el.name, el.type === 'checkbox' ? (el.checked ? '1' : '0') : el.value)
  })

  fetch('/calculate', {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken(),
      'Accept': 'text/vnd.turbo-stream.html'
    },
    body: fd
  })
    .then(r => { if (!r.ok) throw new Error(r.statusText); return r.text() })
    .then(html => { hasCalculated = true; Turbo.renderStreamMessage(html) })
    .catch(err => console.error('Calculate failed:', err))
}
